// FieldVision - BookingWizard.tsx
// Bestillings-wizard for drone og satellit kort
// FIX 2025-12-31: CVR hentes nu fra feature.properties.CVR for korrekt satellit-gemning
// FIX 2026-01-20: Max 4 satellitkort per bestilling (Copernicus rate limiting)

import React, { useState, useEffect } from 'react';
import droneIcon from '../assets/drone.svg';
import satelliteIcon from '../assets/satellite.svg';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { getFieldId, deduplicateFeatures, findFeatureById, getCvrFromFieldId } from '../utils/fieldUtils';

interface Field {
  id: string;
  name: string;
  area_ha: number;
}

interface BookingWizardProps {
  fieldsData: any; // GeoJSON data (CVR fields)
  zoomLoadedFields?: any[]; // Fields loaded from zoom
  bookingSelectedFields: string[]; // Booking-specific selection (managed by App.tsx) - now CVR-Marknr format
  onBookingSelectionChange: (fieldIds: string[]) => void; // Callback to update selection
  highlightedZoomFields?: string[]; // Zoom field IDs from MyFields (for yellow coloring)
  onNavigateToMyFields?: () => void; // Callback to navigate to MyFields view
}

// Simple function to generate SVG path from GeoJSON coordinates
const getFieldThumbnail = (geometry: any) => {
  if (!geometry || !geometry.coordinates || !geometry.coordinates[0]) return null;

  const coords = geometry.coordinates[0];

  // Find bounding box
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  coords.forEach((coord: number[]) => {
    minX = Math.min(minX, coord[0]);
    maxX = Math.max(maxX, coord[0]);
    minY = Math.min(minY, coord[1]);
    maxY = Math.max(maxY, coord[1]);
  });

  // Scale to fit in 40x40 viewBox
  const width = maxX - minX;
  const height = maxY - minY;
  const scale = 35 / Math.max(width, height);

  // Generate SVG path
  const path = coords.map((coord: number[], i: number) => {
    const x = (coord[0] - minX) * scale + 2.5;
    const y = 40 - ((coord[1] - minY) * scale + 2.5); // Flip Y
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(' ') + ' Z';

  return path;
};

const BookingWizard: React.FC<BookingWizardProps> = ({
  fieldsData,
  zoomLoadedFields = [],
  bookingSelectedFields,
  onBookingSelectionChange,
  highlightedZoomFields = [],
  onNavigateToMyFields
}) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [mapType, setMapType] = useState<'ndre' | 'weed' | 'regrowth' | 'special' | null>(null);
  const [source, setSource] = useState<'drone' | 'satellite' | 'other' | null>(null);
  const [customerNotes, setCustomerNotes] = useState('');
  // FEATURE-oensket-dato (2026-06-11): lokal-tidssikker yyyy-mm-dd +N dage
  const fvDatePlusDays = (days: number) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const [desiredReadyDate, setDesiredReadyDate] = useState(fvDatePlusDays(5));
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState<any>(null);
  const [satelliteResult, setSatelliteResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [satelliteUsage, setSatelliteUsage] = useState<{ used: number; limit: number; remaining: number } | null>(null);

  // Fetch satellite usage when satellite is selected
  useEffect(() => {
    if (source === 'satellite') {
      fetchSatelliteUsage();
    }
  }, [source]);

  const fetchSatelliteUsage = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/satellite/usage`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setSatelliteUsage(data.usage);
      }
    } catch (err) {
      console.error('Failed to fetch satellite usage:', err);
    }
  };

  // Combine CVR fields + zoom-loaded fields for lookups, with deduplication using CVR-Marknr
  const getAllFields = () => {
    const cvrFeatures = fieldsData?.features || [];
    const allFeatures = cvrFeatures.concat(zoomLoadedFields);
    // Deduplicate using CVR-Marknr to avoid duplicate key warnings
    return deduplicateFeatures(allFeatures);
  };

  // Check if we have any fields available (CVR fields or zoom-loaded fields)
  const hasAvailableFields = () => {
    const cvrFields = fieldsData?.features?.length || 0;
    const zoomFields = zoomLoadedFields?.length || 0;
    return cvrFields > 0 || zoomFields > 0;
  };

  // If no fields at all, show "Select fields first" message
  if (!hasAvailableFields()) {
    return (
      <div className="w-96 bg-white p-6 shadow-lg">
        <h2 className="text-2xl font-bold mb-6">Bestil kort</h2>

        <div className="text-center py-12">
          <div className="mb-6">
            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-2">Ingen marker tilgængelige</h3>
          <p className="text-gray-600 mb-6">
            Du skal vælge dine CVR marker før du kan bestille.
          </p>

          <button
            onClick={() => onNavigateToMyFields?.()}
            className="bg-[#3c6c34] text-white px-6 py-3 rounded-lg hover:bg-[#2A4C24] transition-colors font-semibold"
          >
            Gå til Mine Marker
          </button>
        </div>
      </div>
    );
  }

  // Get info for selected fields (using booking-specific selection with CVR-Marknr IDs)
  const getSelectedFieldsInfo = () => {
    const allFields = getAllFields();
    if (allFields.length === 0) return [];

    // Use a Set to ensure unique IDs in bookingSelectedFields
    const uniqueSelectedIds = Array.from(new Set(bookingSelectedFields));

    return uniqueSelectedIds.map(fieldId => {
      // Find feature using CVR-Marknr ID
      const feature = findFeatureById(allFields, fieldId);
      if (!feature) return null;

      const marknr = feature.properties.Marknr;
      const markblok = feature.properties.MARKBLOKNR;
      // FIX 2025-12-31: Hent CVR direkte fra feature properties for korrekt satellit-gemning
      // Ved zoom-loaded marker (blaa) sikrer dette at markens CVR bruges, ikke brugerens aktive CVR
      // Fallback til getCvrFromFieldId hvis properties.CVR mangler
      const cvr = feature.properties?.CVR || getCvrFromFieldId(fieldId);
      const arealRaw = feature.properties.GB_AREAL || feature.properties.IMK_areal || feature.properties.GEOMETRISK || 0;
      // Convert to ha if value is too large (likely m2)
      const areal = arealRaw > 1000 ? Math.round((arealRaw / 10000) * 100) / 100 : Math.round(arealRaw * 100) / 100;
      const svgPath = getFieldThumbnail(feature.geometry);

      // Check field type for correct coloring
      const isCvrField = fieldsData?.features?.some(
        (f: any) => getFieldId(f) === fieldId
      );

      // Check if this is a zoom field from MyFields (in highlightedZoomFields)
      // This matches the map's color logic exactly
      const isMyFieldsZoom = !isCvrField && highlightedZoomFields.includes(fieldId);

      return {
        id: fieldId,
        name: marknr ? marknr : `Markblok ${markblok}`,
        marknr: marknr,
        markblok: markblok,
        cvr: cvr, // FIX: Include CVR in field info
        area_ha: areal,
        svgPath: svgPath,
        geometry: feature.geometry,
        isCvrField: isCvrField,
        isMyFieldsZoom: isMyFieldsZoom,
      };
    }).filter(Boolean);
  };

  const selectedFieldsInfo = getSelectedFieldsInfo();
  // Round totalArea to 2 decimals to avoid floating-point accumulation errors
  const totalArea = Math.round(
    selectedFieldsInfo.reduce((sum, field) => sum + (field?.area_ha || 0), 0) * 100
  ) / 100;

  // Opstartsgebyr for drone flyvning
  const STARTUP_FEE = 699;

  // Max antal satellitkort per bestilling (Copernicus rate limiting)
  const MAX_SATELLITE_MAPS = 4;

  // Calculate price based on map type and source
  const getPricePerHa = () => {
    if (source === 'satellite' && mapType === 'ndre') return 0;
    if (mapType === 'weed') return 240;
    if (mapType === 'regrowth') return 240;
    if (mapType === 'special') return 180;
    return 110; // NDRE drone
  };

  const pricePerHa = getPricePerHa();
  // Arealpris (uden opstartsgebyr)
  const areaPrice = Math.round(totalArea * pricePerHa);
  // Total pris inkl. opstartsgebyr (kun for drone)
  const totalPrice = source === 'drone' ? areaPrice + STARTUP_FEE : 0;

  // Check if satellite max limit is exceeded
  const isSatelliteMaxExceeded = source === 'satellite' && bookingSelectedFields.length > MAX_SATELLITE_MAPS;

  const canProceed = () => {
    if (step === 1) return mapType !== null;
    if (step === 2) return source !== null;
    if (step === 3) {
      if (bookingSelectedFields.length === 0) return false;
      // Check satellite monthly limit
      if (source === 'satellite' && satelliteUsage) {
        if (bookingSelectedFields.length > satelliteUsage.remaining) return false;
      }
      // Check satellite max per order limit
      if (isSatelliteMaxExceeded) return false;
      return true;
    }
    return true;
  };

  const handleReset = () => {
    setStep(1);
    setMapType(null);
    setSource(null);
    setOrderSuccess(null);
    setSatelliteResult(null);
    setError('');
    onBookingSelectionChange([]); // Clear booking selection
  };

  // FIX: Process satellite maps with CVR parameter
  // Groups fields by CVR and sends CVR with request to prevent cross-CVR data leakage
  const processSatelliteMaps = async (fields: { marknr: string; cvr: string }[]) => {
    // Group fields by CVR
    const fieldsByCvr = fields.reduce((acc, field) => {
      if (!acc[field.cvr]) {
        acc[field.cvr] = [];
      }
      acc[field.cvr].push(field.marknr);
      return acc;
    }, {} as Record<string, string[]>);

    // Process each CVR group separately
    const results = [];
    for (const [cvr, marknrs] of Object.entries(fieldsByCvr)) {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/satellite/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          marknrs,
          cvr  // FIX: Send CVR with request
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Failed to process CVR ${cvr}`);
      }

      results.push(data);
    }

    // Combine results from all CVR groups
    if (results.length === 1) {
      setSatelliteResult(results[0]);
      return results[0];
    }

    // Merge multiple results
    const combined = {
      success: results.every(r => r.success),
      processed: results.reduce((sum, r) => sum + (r.processed || 0), 0),
      successful: results.reduce((sum, r) => sum + (r.successful || 0), 0),
      failed: results.reduce((sum, r) => sum + (r.failed || 0), 0),
      results: results.flatMap(r => r.results || []),
      usage: results[results.length - 1]?.usage // Use latest usage count
    };

    setSatelliteResult(combined);
    return combined;
  };

  const handleSubmitOrder = async () => {
    setLoading(true);
    setError('');

    try {
      if (source === 'satellite') {
        // SATELLITE FLOW: Process automatically
        // FIX: Extract both marknr and cvr from selected fields
        const fields = selectedFieldsInfo
          .filter(f => f?.marknr && f?.cvr)
          .map(f => ({ marknr: f!.marknr as string, cvr: f!.cvr as string }));

        if (fields.length === 0) {
          throw new Error('Ingen gyldige marker valgt');
        }

        const result = await processSatelliteMaps(fields);

        setOrderSuccess({
          orderNumber: `SAT-${Date.now()}`,
          totalArea: totalArea,
          totalPrice: 0,
          status: 'Færdig',
          source: 'satellite'
        });

        setStep(5); // Show success screen

      } else {
        // DRONE FLOW: Create order for admin handling
        // FEATURE-oensket-dato: guard (input-min daekker normalt, dette er baeltet)
        if (desiredReadyDate && desiredReadyDate < fvDatePlusDays(2)) {
          throw new Error('Ønsket klar-dato skal være mindst 2 dage frem');
        }
        const orderData = {
          serviceType: mapType === 'ndre' ? 'NDVI' : mapType === 'weed' ? 'WEED' : mapType === 'regrowth' ? 'REGROWTH' : 'SPECIAL',
          fields: selectedFieldsInfo.map(field => ({
            name: field?.name || '',
            markblokNumber: field?.markblok || '',
            fieldId: field?.id || '',
            area: field?.area_ha || 0,
            geometry: field?.geometry,
            cvr: field?.cvr || ''  // FIX: Include CVR for mixed-CVR orders
          })),
          customerNotes: customerNotes || null,
          desiredReadyDate: desiredReadyDate || null
        };

        const result = await apiService.createOrder(orderData);

        setOrderSuccess(result);
        setStep(5); // Show success screen
      }
    } catch (err: any) {
      setError(err.message || 'Kunne ikke oprette bestilling');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-96 bg-white p-6 shadow-lg overflow-y-auto">
      <h2 className="text-2xl font-bold mb-6">Bestil kort</h2>

      {/* Progress indicators */}
      <div className="flex justify-between mb-8">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex flex-col items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                step >= s ? 'bg-[#3c6c34] text-white' : 'bg-gray-200 text-gray-500'
              }`}
            >
              {s}
            </div>
            <div className="text-xs mt-1 text-gray-600">
              {s === 1 && 'Kort'}
              {s === 2 && 'Kilde'}
              {s === 3 && 'Marker'}
              {s === 4 && 'Bestil'}
            </div>
          </div>
        ))}
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Success message */}
      {orderSuccess && step === 5 && (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 border-2 border-lime-800 rounded">
            <h3 className="text-xl font-bold text-green-800 mb-2">
              {source === 'satellite' ? <><img src={satelliteIcon} alt="" style={{width: '24px', height: '24px', display: 'inline', verticalAlign: 'middle', marginRight: '8px'}} />Satellitkort klar!</> : 'Bestilling sendt!'}
            </h3>
            <div className="space-y-2 text-sm">
              {source === 'satellite' && satelliteResult && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Antal kort:</span>
                    <span className="font-medium">{satelliteResult.successful} af {satelliteResult.processed}</span>
                  </div>
                  {satelliteResult.results?.filter((r: any) => r.success).map((result: any, idx: number) => (
                    <div key={idx} className="p-2 bg-white rounded border mt-2">
                      <div className="font-medium">{result.marknr}</div>
                      <div className="text-xs text-gray-500">
                        Billede fra: {result.imageDate} ({result.cloudCover?.toFixed(1)}% skyer)
                      </div>
                      <div className="text-xs text-gray-500">
                        {result.zones?.length} zoner • {result.totalHectares?.toFixed(2)} ha
                      </div>
                    </div>
                  ))}
                  <div className="mt-2 p-2 bg-green-50 rounded text-xs">
                    <span className="font-medium">Forbrug:</span> {satelliteResult.usage?.used}/{satelliteResult.usage?.limit} kort denne måned
                  </div>
                </>
              )}
              {source === 'drone' && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ordre nummer:</span>
                    <span className="font-medium">{orderSuccess.orderId ? `#${orderSuccess.orderId}` : orderSuccess.orderNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total areal:</span>
                    <span className="font-medium">{Number(orderSuccess.totalArea).toFixed(2)} ha</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total pris:</span>
                    <span className="font-medium text-green-600">{Math.round(orderSuccess.totalPrice)} kr</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 italic">* Tillægges kørsel</div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="font-medium">{orderSuccess.status}</span>
                  </div>
                </>
              )}
            </div>
          </div>
          <button
            onClick={handleReset}
            className="btn-primary w-full py-3"
          >
            Lav ny bestilling
          </button>
          {source === 'satellite' && (
            <button
              onClick={() => onNavigateToMyFields?.()}
              className="btn-secondary w-full py-3"
            >
              Se kort i Mine Marker
            </button>
          )}
        </div>
      )}

      {/* Step content */}
      {step < 5 && (
        <div>
          {step === 1 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Vælg korttype</h3>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setMapType('ndre');
                    setSource(null); // Reset source when changing map type
                  }}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                    mapType === 'ndre'
                      ? 'border-lime-800 bg-green-50'
                      : 'border-gray-300 hover:border-lime-800'
                  }`}
                >
                  <div className="font-semibold">NDRE kort</div>
                  <div className="text-sm text-gray-600">Vegetations- og biomasseanalyse</div>
                </button>
                <button
                  onClick={() => {
                    setMapType('weed');
                    setSource('drone'); // Auto-select drone for weed
                  }}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                    mapType === 'weed'
                      ? 'border-lime-800 bg-green-50'
                      : 'border-gray-300 hover:border-lime-800'
                  }`}
                >
                  <div className="font-semibold">Ukrudtskort</div>
                  <div className="text-sm text-gray-600">AI-baseret ukrudtsdetektion</div>
                  <div className="text-xs text-gray-400 mt-1">Kun drone (høj opløsning krævet)</div>
                </button>
                <button
                  onClick={() => {
                    setMapType('regrowth');
                    setSource('drone'); // Auto-select drone for regrowth
                  }}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                    mapType === 'regrowth'
                      ? 'border-lime-800 bg-green-50'
                      : 'border-gray-300 hover:border-lime-800'
                  }`}
                >
                  <div className="font-semibold">Genvækstkort</div>
                  <div className="text-sm text-gray-600">AI-baseret genvækstkort</div>
                  <div className="text-xs text-gray-400 mt-1">Kun drone</div>
                </button>
                <button
                  onClick={() => {
                    setMapType('special');
                    setSource(null); // Reset source - user must choose
                  }}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                    mapType === 'special'
                      ? 'border-lime-800 bg-green-50'
                      : 'border-gray-300 hover:border-lime-800'
                  }`}
                >
                  <div className="font-semibold">Specialkort</div>
                  <div className="text-sm text-gray-600">Aftalt med Dansk Planteinspektion</div>
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Vælg datakilde</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setSource('drone')}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                    source === 'drone'
                      ? 'border-lime-800 bg-green-50'
                      : 'border-gray-300 hover:border-lime-800'
                  }`}
                >
                  <div className="font-semibold"><img src={droneIcon} alt="" style={{width: '20px', height: '20px', display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />Droneflyvning</div>
                  <div className="text-sm text-gray-600">Høj opløsning (1-2 cm GSD)</div>
                  <div className="text-sm text-green-600 mt-1">
                    {mapType === 'weed' || mapType === 'regrowth' ? '240 kr/ha' : mapType === 'special' ? '180 kr/ha' : '110 kr/ha'} + {STARTUP_FEE} kr opstart
                  </div>
                </button>

                {(mapType === 'ndre' || mapType === 'special') && (
                  <button
                    onClick={() => setSource('satellite')}
                    className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                      source === 'satellite'
                        ? 'border-lime-800 bg-green-50'
                        : 'border-gray-300 hover:border-lime-800'
                    }`}
                  >
                    <div className="font-semibold"><img src={satelliteIcon} alt="" style={{width: '20px', height: '20px', display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />Satellitdata</div>
                    <div className="text-sm text-gray-600">Sentinel-2 (10m opløsning)</div>
                    {mapType === 'ndre' && (
                      <>
                        <div className="text-sm text-green-600 mt-1">Gratis</div>
                        <div className="text-xs text-gray-400 mt-1">
                          Max {MAX_SATELLITE_MAPS} kort per bestilling • Max 15 kort/måned
                        </div>
                        {satelliteUsage && (
                          <div className="text-xs text-green-600 mt-1">
                            Forbrug: {satelliteUsage.used}/{satelliteUsage.limit} denne måned
                          </div>
                        )}
                      </>
                    )}
                    {mapType === 'special' && (
                      <div className="text-sm text-green-600 mt-1">180 kr/ha + {STARTUP_FEE} kr opstart</div>
                    )}
                  </button>
                )}

                {mapType === 'special' && (
                  <button
                    onClick={() => setSource('other')}
                    className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                      source === 'other'
                        ? 'border-lime-800 bg-green-50'
                        : 'border-gray-300 hover:border-lime-800'
                    }`}
                  >
                    <div className="font-semibold">Anden kilde</div>
                    <div className="text-sm text-gray-600">Specialaftale</div>
                    <div className="text-sm text-green-600 mt-1">180 kr/ha + {STARTUP_FEE} kr opstart</div>
                  </button>
                )}

                {(mapType === 'weed' || mapType === 'regrowth') && (
                  <div className="p-4 bg-gray-100 rounded-lg text-gray-500 text-sm">
                    <div className="font-semibold"><img src={satelliteIcon} alt="" style={{width: '20px', height: '20px', display: 'inline', verticalAlign: 'middle', marginRight: '6px', opacity: 0.5}} />Satellit ikke tilgængelig</div>
                    <div className="text-xs mt-1">
                      {mapType === 'weed' ? 'Ukrudtsdetektion' : 'Genvækstkort'} kræver høj opløsning som kun drone kan levere.
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Vælg marker</h3>
              <p className="text-gray-500 text-sm mb-4">Klik på marker på kortet for at tilføje til bestillingen</p>

              {/* Satellite max per order warning */}
              {isSatelliteMaxExceeded && (
                <div className="mb-4 p-3 rounded text-sm bg-red-50 border border-red-200 text-red-700">
                  <div className="font-medium mb-1">Du kan bestille maks {MAX_SATELLITE_MAPS} satellitkort ad gangen</div>
                  <div>
                    Du har valgt {bookingSelectedFields.length} marker.
                    Fjern {bookingSelectedFields.length - MAX_SATELLITE_MAPS} marker eller opdel i flere bestillinger.
                  </div>
                </div>
              )}

              {/* Satellite monthly limit warning */}
              {source === 'satellite' && satelliteUsage && !isSatelliteMaxExceeded && (
                <div className={`mb-4 p-3 rounded text-sm ${
                  bookingSelectedFields.length > satelliteUsage.remaining
                    ? 'bg-red-50 border border-red-200 text-red-700'
                    : 'bg-green-50 border border-blue-200 text-blue-700'
                }`}>
                  {bookingSelectedFields.length > satelliteUsage.remaining ? (
                    <>
                      Du har valgt {bookingSelectedFields.length} marker, men har kun {satelliteUsage.remaining} kort tilbage denne måned.
                    </>
                  ) : (
                    <>
                      <img src={satelliteIcon} alt="" style={{width: '16px', height: '16px', display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />{satelliteUsage.remaining} satellitkort tilbage denne måned
                    </>
                  )}
                </div>
              )}

              {selectedFieldsInfo.length === 0 ? (
                <div className="p-4 bg-gray-50 rounded border border-dashed border-gray-300 text-center">
                  <p className="text-gray-400">Ingen marker valgt endnu</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedFieldsInfo.map((field) => (
                    <div key={field?.id} className="p-3 bg-gray-50 rounded border flex items-center gap-3">
                      {field?.svgPath && (
                        <svg width="40" height="40" viewBox="0 0 40 40" className="flex-shrink-0">
                          <path d={field.svgPath} fill={field?.isCvrField ? "#4783b6" : "#a0532a"} stroke={field?.isCvrField ? "#4783b6" : "#a0532a"} strokeWidth="1" />
                        </svg>
                      )}
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {field?.name}
                          {!field?.isCvrField && (
                            <span className="ml-2 text-xs text-gray-500">(uden for CVR)</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">{field?.area_ha.toFixed(2)} ha</div>
                      </div>
                    </div>
                  ))}
                  <div className="mt-4 p-3 bg-green-50 rounded border border-green-200">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">Total areal:</span>
                      <span>{totalArea.toFixed(2)} ha</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Bestillingsoversigt</h3>

              {/* Satellite max per order warning in Step 4 */}
              {isSatelliteMaxExceeded && (
                <div className="mb-4 p-3 rounded text-sm bg-red-50 border border-red-200 text-red-700">
                  <div className="font-medium mb-1">Du kan bestille maks {MAX_SATELLITE_MAPS} satellitkort ad gangen</div>
                  <div>
                    Du har valgt {bookingSelectedFields.length} marker.
                    Gå tilbage og fjern {bookingSelectedFields.length - MAX_SATELLITE_MAPS} marker.
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded">
                  <div className="text-sm text-gray-600 mb-1">Korttype</div>
                  <div className="font-medium">{mapType === 'ndre' ? 'NDRE kort' : mapType === 'weed' ? 'Ukrudtskort' : mapType === 'regrowth' ? 'Genvækstkort' : 'Specialkort'}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded">
                  <div className="text-sm text-gray-600 mb-1">Datakilde</div>
                  <div className="font-medium">
                    {source === 'drone' ? <><img src={droneIcon} alt="" style={{width: '20px', height: '20px', display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />Droneflyvning</> : <><img src={satelliteIcon} alt="" style={{width: '20px', height: '20px', display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />Satellitdata</>}
                  </div>
                  {source === 'satellite' && (
                    <div className="text-xs text-green-600 mt-1">Leveres automatisk med det samme</div>
                  )}
                </div>
                <div className="p-4 bg-gray-50 rounded">
                  <div className="text-sm text-gray-600 mb-1">Valgte marker</div>
                  <div className="space-y-2 mt-2">
                    {selectedFieldsInfo.map((field) => (
                      <div key={field?.id} className="flex items-center gap-2">
                        {field?.svgPath && (
                          <svg width="30" height="30" viewBox="0 0 40 40" className="flex-shrink-0">
                            <path d={field.svgPath} fill={field?.isCvrField ? "#4783b6" : "#a0532a"} stroke={field?.isCvrField ? "#4783b6" : "#a0532a"} strokeWidth="1" />
                          </svg>
                        )}
                        <div className="text-sm">
                          {field?.name} ({field?.area_ha.toFixed(2)} ha)
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {source === 'drone' && (
                  <div className="p-4 bg-gray-50 rounded">
                    <div className="text-sm text-gray-600 mb-1">Kort ønskes klar senest</div>
                    <input
                      type="date"
                      value={desiredReadyDate}
                      min={fvDatePlusDays(2)}
                      onChange={(e) => setDesiredReadyDate(e.target.value)}
                      className="w-full mt-1 p-2 border border-gray-300 rounded text-sm bg-white cursor-pointer"
                    />
                    {desiredReadyDate && desiredReadyDate < fvDatePlusDays(2) ? (
                      <div className="text-xs text-red-600 mt-1">Vi skal bruge mindst 2 dages varsel — vælg en senere dato</div>
                    ) : (
                      <div className="text-xs text-gray-500 mt-1">Vi foreslår 5 dage frem — tryk på datoen for at ændre</div>
                    )}
                  </div>
                )}
                <div className="p-4 bg-gray-50 rounded">
                  <div className="text-sm text-gray-600 mb-1">Yderligere information</div>
                  <textarea
                    value={customerNotes}
                    onChange={(e) => setCustomerNotes(e.target.value)}
                    placeholder="Skriv evt. bemærkninger til bestillingen..."
                    className="w-full mt-1 p-2 border border-gray-300 rounded text-sm resize-none"
                    rows={2}
                  />
                </div>
                <div className={`p-4 rounded border-2 ${
                  source === 'satellite' ? 'bg-green-50 border-lime-800' : 'bg-green-50 border-lime-800'
                }`}>
                  <div className="text-sm text-gray-600 mb-1">
                    {source === 'satellite' ? 'Pris' : 'Estimeret pris'}
                  </div>
                  {source === 'drone' && (
                    <>
                      <div className="flex justify-between text-sm mb-1">
                        <span>{totalArea.toFixed(2)} ha × {pricePerHa} kr/ha</span>
                        <span>{areaPrice} kr</span>
                      </div>
                      <div className="flex justify-between text-sm mb-2">
                        <span>Opstartsgebyr</span>
                        <span>{STARTUP_FEE} kr</span>
                      </div>
                      <div className="border-t border-lime-800 pt-2 mt-2">
                        <div className="flex justify-between text-2xl font-bold text-green-600">
                          <span>Total</span>
                          <span>{totalPrice} kr</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-2 italic">
                        * Prisen tillægges kørsel til pilot
                      </div>
                    </>
                  )}
                  {source === 'satellite' && (
                    <>
                      <div className="text-2xl font-bold text-green-600">Gratis</div>
                      <div className="text-xs text-gray-500 mt-2">
                        {selectedFieldsInfo.length} kort af {satelliteUsage?.remaining || 15} tilbage denne måned
                      </div>
                    </>
                  )}
                </div>
                <div className="flex items-start gap-2 mt-4">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#3c6c34]"
                  />
                  <label htmlFor="acceptTerms" className="text-sm text-gray-700">
                    Jeg accepterer <a href="https://planteinspektion.dk/salgs-og-leveringsbetingelser/" target="_blank" rel="noopener noreferrer" className="text-[#3c6c34] underline hover:text-[#2A4C24]">handelsbetingelser</a>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Navigation buttons */}
      {step < 5 && (
        <div className="mt-6 space-y-2">
          {step < 4 ? (
            <>
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                  canProceed()
                    ? 'bg-[#3c6c34] text-white hover:bg-[#2A4C24]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Næste
              </button>
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="w-full py-3 rounded-lg font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  Tilbage
                </button>
              )}
            </>
          ) : (
            <>
              <button
                onClick={handleSubmitOrder}
                disabled={loading || !acceptedTerms || isSatelliteMaxExceeded}
                className={`w-full py-3 rounded-lg font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                  source === 'satellite'
                    ? 'bg-[#3c6c34] hover:bg-[#2A4C24]'
                    : 'bg-[#3c6c34] hover:bg-[#2A4C24]'
                }`}
              >
                {loading
                  ? (source === 'satellite' ? 'Henter kort...' : 'Sender...')
                  : (source === 'satellite' ? <><img src={satelliteIcon} alt="" style={{width: '20px', height: '20px', display: 'inline', verticalAlign: 'middle', marginRight: '6px', filter: 'brightness(0) invert(1)'}} />Hent satellitkort</> : 'Send bestilling')
                }
              </button>
              <button
                onClick={handleReset}
                className="w-full py-3 rounded-lg font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Start forfra
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingWizard;
